import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Chip, Typography, useTheme } from '@mui/material';
import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { CTableWrapper, DTabs, ICustomTableWrapperProps } from '../../../dum';
import { formatFloat } from '../../../../helpers';
import {
    useProductionPerformanceHourlyRunsLazyQuery,
    useProductionPerformanceRunsLazyQuery,
} from '../../../../services';
import { useFocusPerfRun, usePerfRunFocus } from './perf-filters-context';
import { SHIFT_HOURS } from '../../../smart/production-performance';

const COLORS = [
    '#1565c0',
    '#e65100',
    '#2e7d32',
    '#6a1b9a',
    '#00838f',
    '#ad1457',
    '#f9a825',
    '#4527a0',
];

// How many top series (by total kilos) are pre-selected on the scatter.
const DEFAULT_SELECTED = 3;
// Std dev on 1–2 runs is meaningless — show a dash below this run count.
const MIN_RUNS_FOR_STDDEV = 3;

// The scatter/series stats can be grouped either by employee or by product.
// The rule is automatic (no "Comparar por" toggle): a product sub-filter means
// "who runs THIS product" → group by employee; otherwise group by product.
type SeriesMode = 'employee' | 'product';

interface ScatterPoint {
    x: number;
    y: number;
    date: string;
    monthKey: string;
    orderProductionId: number;
    // This corrida belongs to the highlighted production (see props).
    highlighted: boolean;
}

interface ScatterClickPoint extends Partial<ScatterPoint> {
    payload?: ScatterPoint;
}

// One point going into the aggregation: a run credited to a series (employee or
// product). In employee mode a point = one (line × employee) row (full credit to
// each employee). In product mode the employee fan-out is collapsed first so a
// point = one corrida-line (see buildStats callers).
interface PointEntry {
    key: number;
    name: string;
    orderProductionId: number;
    kilos: number;
    hours: number;
    waste: number;
    date: Date | null;
    highlighted: boolean;
}

interface SeriesStat {
    key: number;
    name: string;
    runs: number;
    totalKilos: number;
    avgKilos: number;
    stdDev: number | null;
    min: number;
    max: number;
    hours: number;
    kgHr: number | null;
    mermaPct: number;
    points: ScatterPoint[];
    // The series ran the highlighted production.
    inHighlighted: boolean;
}

export interface IProductionPerformanceAnalysis {
    machineId?: number | null;
    productId?: number | null;
    // Server-side window — the Desde/Hasta pair owned by the caller (the
    // Rendimiento page, or the grading dialog) flows in here; the panel keeps no
    // date picker of its own. Both callers default `fromDate` to
    // HOURLY_DATA_EPOCH: pre-epoch corridas carry hours = 0, so leaving them in
    // adds kilos to every kg/hr numerator and nothing to its denominator.
    fromDate?: string | null;
    toDate?: string | null;
    // When set (the per-record "grading" view), this production's corridas get
    // a star marker on the scatter, its series are flagged in the table, and the
    // default scatter selection is those series instead of the top performers —
    // so the record is judged against the rest of the combo's history in the
    // selected window. A record outside that window has no star and no flagged
    // series; widen Desde/Hasta to bring it back in.
    highlightProductionId?: number | null;
    // "Solo producciones de un solo producto" — owned by the caller (page or
    // dialog) and lifted above this panel so it drives the KPI and scatter
    // together. It filters ONLY the PACKED side (Producción kg/hr and the
    // scatter) to product_count === 1 runs, whose per-product hours are clean.
    // Consumo kg/hr and the Corridas list stay over all runs, so the consumed
    // figures still agree with the Rendimiento tab.
    singleProductOnly?: boolean;
}

interface HourlyRunRow {
    orderProductionId: number;
    date: string | null;
    kilosProduced: number;
    groupsProduced: number;
    hoursProduced: number;
    kgHrProduced: number | null;
    kilosConsumed: number;
    hoursConsumed: number;
    kgHrConsumed: number | null;
    // Distinct products the production ran under the current filters — the row
    // sums them all.
    productCount: number;
}

// Map the raw hourly rows into display rows (kg/hr per row = kilos ÷ hours, or
// null when hours are 0). Shared by the KPI headline (panel) and the Corridas
// table so both read the same shape.
function mapHourlyRows(
    raw: {
        order_production_id: number;
        date?: Date | string | null;
        kilos_produced: number;
        groups_produced: number;
        hours_produced: number;
        kilos_consumed: number;
        hours_consumed: number;
        product_count: number;
    }[],
): HourlyRunRow[] {
    return raw.map((r) => ({
        orderProductionId: r.order_production_id,
        // start_date is a floating CALENDAR DATE stamped at midnight UTC (see
        // getStringFromDate/formatDate). Read it back in UTC so the day doesn't
        // slip to the previous one in Mérida's UTC-6 offset.
        date: r.date ? dayjs.utc(r.date).format('DD/MM/YYYY (dddd)') : null,
        kilosProduced: r.kilos_produced,
        groupsProduced: r.groups_produced,
        hoursProduced: r.hours_produced,
        kgHrProduced:
            r.hours_produced > 0 ? r.kilos_produced / r.hours_produced : null,
        kilosConsumed: r.kilos_consumed,
        hoursConsumed: r.hours_consumed,
        kgHrConsumed:
            r.hours_consumed > 0 ? r.kilos_consumed / r.hours_consumed : null,
        productCount: r.product_count,
    }));
}

// Shared analysis panel: works under ANY combination of machine / product
// filters. A KPI headline (Producción kg/hr · Consumo kg/hr) is always
// visible and recomputed on every filter change; below it two tabs hold the
// scatter ("Gráfica") and the per-run hourly table ("Corridas"). Both the
// headline and the Corridas table read the same hourly query (fetched once here,
// at panel level), so switching tabs never refetches. DTabs only mounts the
// active tab, giving the Gráfica scatter its lazy fetch.
export function ProductionPerformanceAnalysis({
    machineId = null,
    productId = null,
    fromDate = null,
    toDate = null,
    highlightProductionId,
    singleProductOnly = false,
}: IProductionPerformanceAnalysis) {
    const hasFilter = !!(machineId || productId);
    const runFocus = usePerfRunFocus();
    const [activeAnalysisTab, setActiveAnalysisTab] = useState(0);

    useEffect(() => {
        if (runFocus?.source === 'audit') setActiveAnalysisTab(0);
    }, [runFocus]);

    const [queryHourly, { data: hourlyData }] =
        useProductionPerformanceHourlyRunsLazyQuery();

    // At least one id is required (the backend throws otherwise). Refetch on any
    // filter change — the dates now bound the window server-side.
    useEffect(() => {
        if (hasFilter) {
            void queryHourly({
                variables: {
                    machineId,
                    productId,
                    fromDate,
                    toDate,
                },
            });
        }
    }, [hasFilter, machineId, productId, fromDate, toDate, queryHourly]);

    const hourlyRows = useMemo<HourlyRunRow[]>(
        () => mapHourlyRows(hourlyData?.getMachineHourlyRuns ?? []),
        [hourlyData],
    );

    // Totals-over-totals ratios: sum the numerators and denominators across all
    // rows, then divide once — a run with 0 hours contributes kilos but no hours,
    // exactly as decided. Shows "—" when the summed hours are 0.
    //
    // When grading a record (highlightProductionId set), that production is
    // dropped before summing, so the headline is the baseline the record is
    // judged against rather than an average that already contains it. This is
    // what makes the dialog's KPI and the form's Rendimiento tab report the same
    // quantity — path A holds every row, so the exclusion is a filter, not a
    // second query. The Corridas table below still lists the record's own run.
    const kpi = useMemo(() => {
        const rows =
            highlightProductionId != null
                ? hourlyRows.filter(
                      (r) => r.orderProductionId !== highlightProductionId,
                  )
                : hourlyRows;
        // "Solo un producto" cleans ONLY the packed numbers: a multi-product
        // production captures hours on its first packed row alone, so its
        // per-product kg/hr is unrecoverable. The consumed side has no such flaw
        // — its hours are per production — so Consumo kg/hr keeps every run and
        // stays comparable to the Rendimiento tab.
        const packedRows = singleProductOnly
            ? rows.filter((r) => r.productCount === 1)
            : rows;
        const packed = packedRows.reduce(
            (acc, r) => {
                acc.kilos += r.kilosProduced;
                acc.groups += r.groupsProduced;
                acc.hours += r.hoursProduced;
                return acc;
            },
            { kilos: 0, groups: 0, hours: 0 },
        );
        const consumed = rows.reduce(
            (acc, r) => {
                acc.kilos += r.kilosConsumed;
                acc.hours += r.hoursConsumed;
                return acc;
            },
            { kilos: 0, hours: 0 },
        );
        const kgHrProduced =
            packed.hours > 0 ? packed.kilos / packed.hours : null;
        // Bultos/hr rides the same packed hours as kg/hr — the floor's primary
        // unit. Null when no bultos were captured on the packed rows (e.g. a
        // purely weighed extrusión combo), so it reads "—" instead of a
        // misleading 0.
        const bultosHrProduced =
            packed.hours > 0 && packed.groups > 0
                ? packed.groups / packed.hours
                : null;
        return {
            kgHrProduced,
            bultosHrProduced,
            // Per-shift projections (× 8 h) — the tangible "one turno" figure the
            // floor plans in, alongside the hourly rate.
            kg8hProduced: kgHrProduced === null ? null : kgHrProduced * SHIFT_HOURS,
            bultos8hProduced:
                bultosHrProduced === null ? null : bultosHrProduced * SHIFT_HOURS,
            kgHrConsumed:
                consumed.hours > 0 ? consumed.kilos / consumed.hours : null,
        };
    }, [hourlyRows, highlightProductionId, singleProductOnly]);

    const items = useMemo(
        () => [
            {
                title: 'Gráfica',
                content: () => (
                    <GraficaTab
                        machineId={machineId}
                        productId={productId}
                        fromDate={fromDate}
                        toDate={toDate}
                        highlightProductionId={highlightProductionId}
                        singleProductOnly={singleProductOnly}
                    />
                ),
            },
            {
                title: 'Corridas',
                content: () => (
                    <CorridasTab
                        machineId={machineId}
                        productId={productId}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                ),
            },
        ],
        // Only the stable filter props — NOT the hourly data. DTabs renders each
        // tab's `content` by function reference, so rebuilding this array remounts
        // the tabs; keeping it stable while data loads avoids remounting Gráfica
        // (which would drop its scatter selection and refetch). singleProductOnly
        // is a filter, so a remount on toggle is intended.
        [
            machineId,
            productId,
            fromDate,
            toDate,
            highlightProductionId,
            singleProductOnly,
        ],
    );

    if (!hasFilter) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant={'body1'} sx={{ color: 'text.secondary' }}>
                    Selecciona una máquina o un producto para analizar el
                    rendimiento.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    gap: 4,
                    mb: 1,
                }}
            >
                <Box>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Producción kg/hr
                    </Typography>
                    <Typography variant={'h5'}>
                        {kpi.kgHrProduced === null
                            ? '—'
                            : formatFloat(kpi.kgHrProduced)}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Producción kg / 8 h
                    </Typography>
                    <Typography variant={'h5'}>
                        {kpi.kg8hProduced === null
                            ? '—'
                            : formatFloat(kpi.kg8hProduced)}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Producción bultos/hr
                    </Typography>
                    <Typography variant={'h5'}>
                        {kpi.bultosHrProduced === null
                            ? '—'
                            : formatFloat(kpi.bultosHrProduced)}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Producción bultos / 8 h
                    </Typography>
                    <Typography variant={'h5'}>
                        {kpi.bultos8hProduced === null
                            ? '—'
                            : formatFloat(kpi.bultos8hProduced)}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Consumo kg/hr
                    </Typography>
                    <Typography variant={'h5'}>
                        {kpi.kgHrConsumed === null
                            ? '—'
                            : formatFloat(kpi.kgHrConsumed)}
                    </Typography>
                </Box>
            </Box>

            {highlightProductionId != null ? (
                <Typography
                    variant={'caption'}
                    sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                >
                    El promedio excluye esta orden de producción, para
                    compararla contra el resto de su historial.
                </Typography>
            ) : null}

            {productId != null ? (
                <Typography
                    variant={'caption'}
                    sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                >
                    Cálculo filtrado por el producto seleccionado. El consumo
                    kg/hr sigue siendo el total de las corridas (el material
                    consumido no se atribuye a un solo producto).
                </Typography>
            ) : null}

            <DTabs
                items={items}
                value={activeAnalysisTab}
                onChange={setActiveAnalysisTab}
            />
        </>
    );
}

// Aggregate point entries into per-series stats.
function buildStats(entries: PointEntry[]): SeriesStat[] {
    const byKey = new Map<
        number,
        {
            name: string;
            kilos: number[];
            hours: number;
            waste: number;
            points: ScatterPoint[];
            inHighlighted: boolean;
        }
    >();

    for (const en of entries) {
        let g = byKey.get(en.key);
        if (!g) {
            g = {
                name: en.name,
                kilos: [],
                hours: 0,
                waste: 0,
                points: [],
                inHighlighted: false,
            };
            byKey.set(en.key, g);
        }
        g.kilos.push(en.kilos);
        g.hours += en.hours;
        g.waste += en.waste;
        if (en.highlighted) {
            g.inHighlighted = true;
        }
        if (en.date) {
            // See UTC note above — read the floating calendar date back in UTC.
            const d = dayjs.utc(en.date);
            g.points.push({
                x: d.valueOf(),
                y: en.kilos,
                // Weekday in parens, e.g. "14/07/2026 (lunes)". Global dayjs
                // locale is 'es', so dddd renders the Spanish weekday.
                date: d.format('DD/MM/YYYY (dddd)'),
                monthKey: d.format('YYYY-MM'),
                orderProductionId: en.orderProductionId,
                highlighted: en.highlighted,
            });
        }
    }

    const result: SeriesStat[] = [];
    byKey.forEach((g, key) => {
        const n = g.kilos.length;
        const totalKilos = g.kilos.reduce((s, k) => s + k, 0);
        const avgKilos = n > 0 ? totalKilos / n : 0;
        // Sample standard deviation (n − 1). Needs ≥ 2 runs to exist and
        // ≥ MIN_RUNS_FOR_STDDEV to be worth showing.
        let stdDev: number | null = null;
        if (n >= MIN_RUNS_FOR_STDDEV) {
            const variance =
                g.kilos.reduce((s, k) => s + (k - avgKilos) ** 2, 0) / (n - 1);
            stdDev = Math.sqrt(variance);
        }
        result.push({
            key,
            name: g.name,
            runs: n,
            totalKilos,
            avgKilos,
            stdDev,
            min: n > 0 ? Math.min(...g.kilos) : 0,
            max: n > 0 ? Math.max(...g.kilos) : 0,
            hours: g.hours,
            kgHr: g.hours > 0 ? totalKilos / g.hours : null,
            mermaPct: totalKilos > 0 ? (g.waste / totalKilos) * 100 : 0,
            points: g.points,
            inHighlighted: g.inHighlighted,
        });
    });

    result.sort((a, b) => b.totalKilos - a.totalKilos);
    return result;
}

// "Gráfica" tab: scatter (x = date, y = kilos/corrida) + a per-series stats
// table with "Ver" checkboxes. Series are employees (a product is filtered) or
// products (otherwise) — the automatic rule, no toggle.
function GraficaTab({
    machineId,
    productId,
    fromDate,
    toDate,
    highlightProductionId,
    singleProductOnly = false,
}: IProductionPerformanceAnalysis) {
    const theme = useTheme();
    const focusPerfRun = useFocusPerfRun();
    const runFocus = usePerfRunFocus();
    const graphRef = useRef<HTMLDivElement | null>(null);
    const seriesMode: SeriesMode = productId ? 'employee' : 'product';

    const [selectedKeys, setSelectedKeys] = useState<Set<number>>(new Set());

    const [queryRuns, { data: runsData, loading: runsLoading }] =
        useProductionPerformanceRunsLazyQuery();

    useEffect(() => {
        void queryRuns({
            variables: {
                machineId,
                productId,
                fromDate,
                toDate,
            },
        });
    }, [machineId, productId, fromDate, toDate, queryRuns]);

    const stats = useMemo<SeriesStat[]>(() => {
        const allRuns = runsData?.getMachineProductEmployeeRuns ?? [];
        // The scatter is packed throughput, so the single-product filter (lifted
        // from the page/dialog) applies in both series modes: keep only runs
        // whose production made a single product — product_count here is the
        // whole-production count, so it holds even with a product selected.
        const runs = singleProductOnly
            ? allRuns.filter((r) => r.product_count === 1)
            : allRuns;

        const isHighlighted = (orderProductionId: number) =>
            highlightProductionId != null &&
            orderProductionId === highlightProductionId;

        if (seriesMode === 'employee') {
            // One entry per (line × employee) row — full credit to each employee.
            const entries: PointEntry[] = runs.map((r) => ({
                key: r.employee_id,
                name: r.employee_name,
                orderProductionId: r.order_production_id,
                kilos: r.kilos,
                hours: r.hours ?? 0,
                waste: r.waste_share,
                date: r.date,
                highlighted: isHighlighted(r.order_production_id),
            }));
            return buildStats(entries);
        }

        // Product mode: the runs come fanned out per employee, so collapse to
        // unique corrida-lines first (else a multi-employee production would
        // count its kilos twice). kilos/hours are taken once per line; waste is
        // SUMMED across the line's employee rows to rebuild the full line waste
        // (each row carries only its 1/emp_count share).
        const lineMap = new Map<
            string,
            {
                productId: number;
                productName: string;
                orderProductionId: number;
                kilos: number;
                hours: number;
                waste: number;
                date: Date | null;
                highlighted: boolean;
            }
        >();
        for (const r of runs) {
            const lineKey = `${r.order_production_id}-${r.product_id}`;
            let line = lineMap.get(lineKey);
            if (!line) {
                line = {
                    productId: r.product_id,
                    productName: r.product_description,
                    orderProductionId: r.order_production_id,
                    kilos: r.kilos,
                    hours: r.hours ?? 0,
                    waste: 0,
                    date: r.date,
                    highlighted: isHighlighted(r.order_production_id),
                };
                lineMap.set(lineKey, line);
            }
            line.waste += r.waste_share;
        }
        // Array.from instead of spread: the CRA tsconfig target predates
        // downlevelIteration, so spreading a Map iterator doesn't compile.
        const entries: PointEntry[] = Array.from(lineMap.values()).map(
            (line) => ({
                key: line.productId,
                name: line.productName,
                orderProductionId: line.orderProductionId,
                kilos: line.kilos,
                hours: line.hours,
                waste: line.waste,
                date: line.date,
                highlighted: line.highlighted,
            }),
        );
        return buildStats(entries);
    }, [runsData, seriesMode, singleProductOnly, highlightProductionId]);

    // Pre-select whenever a fresh set of stats arrives: the highlighted
    // production's series when grading a record, else the top performers.
    useEffect(() => {
        const highlighted = stats.filter((s) => s.inHighlighted);
        const preselected =
            highlighted.length > 0
                ? highlighted
                : stats.slice(0, DEFAULT_SELECTED);
        setSelectedKeys(new Set(preselected.map((s) => s.key)));
    }, [stats]);

    // An audit row can belong to a series that is currently unchecked. Make
    // every matching series visible before drawing the focused point(s).
    useEffect(() => {
        if (runFocus?.source !== 'audit') return;
        const matchingKeys = stats
            .filter((s) =>
                s.points.some(
                    (p) => p.orderProductionId === runFocus.orderProductionId,
                ),
            )
            .map((s) => s.key);
        if (matchingKeys.length === 0) return;
        setSelectedKeys((current) => {
            const next = new Set(current);
            matchingKeys.forEach((key) => next.add(key));
            return next;
        });
        graphRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }, [runFocus, stats]);

    const toggleSelected = (key: number) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const selectedStats = useMemo(
        () => stats.filter((s) => selectedKeys.has(s.key)),
        [stats, selectedKeys],
    );
    const hasFocusedRun =
        runFocus != null &&
        stats.some((s) =>
            s.points.some(
                (p) => p.orderProductionId === runFocus.orderProductionId,
            ),
        );

    const handleScatterClick = (point: ScatterClickPoint) => {
        const run = point.payload ?? point;
        if (run.orderProductionId == null || !run.monthKey) return;
        focusPerfRun({
            orderProductionId: run.orderProductionId,
            monthKey: run.monthKey,
            source: 'graph',
        });
    };

    const seriesLabel = seriesMode === 'employee' ? 'Empleado' : 'Producto';

    const tableItems = useMemo<
        ICustomTableWrapperProps<SeriesStat>['items']
    >(() => {
        const colorFor = (key: number) => {
            const idx = stats.findIndex((s) => s.key === key);
            return COLORS[idx % COLORS.length];
        };
        return [
            {
                title: 'Ver',
                textAlign: 'center',
                content: (s) => (
                    <Checkbox
                        size={'small'}
                        checked={selectedKeys.has(s.key)}
                        onChange={() => toggleSelected(s.key)}
                        sx={{
                            p: 0,
                            color: colorFor(s.key),
                            '&.Mui-checked': { color: colorFor(s.key) },
                        }}
                    />
                ),
            },
            {
                title: seriesLabel,
                textAlign: 'left',
                cellGrow: 2,
                content: (s) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        {s.name}
                        {s.inHighlighted ? (
                            <Chip
                                size={'small'}
                                color={'primary'}
                                variant={'outlined'}
                                label={'esta orden'}
                            />
                        ) : null}
                    </Box>
                ),
            },
            {
                title: 'Corridas',
                textAlign: 'right',
                content: (s) => formatFloat(s.runs),
            },
            {
                title: 'Total kg',
                textAlign: 'right',
                content: (s) => formatFloat(s.totalKilos),
            },
            {
                title: 'Promedio kg/corrida',
                textAlign: 'right',
                content: (s) => formatFloat(s.avgKilos),
            },
            {
                title: 'Desv. est.',
                textAlign: 'right',
                content: (s) =>
                    s.stdDev === null ? '—' : formatFloat(s.stdDev),
            },
            {
                title: 'Rango (min–max)',
                textAlign: 'right',
                content: (s) => `${formatFloat(s.min)} – ${formatFloat(s.max)}`,
            },
            {
                title: 'Horas',
                textAlign: 'right',
                content: (s) => formatFloat(s.hours),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                content: (s) => (s.kgHr === null ? '—' : formatFloat(s.kgHr)),
            },
            {
                title: 'Merma %',
                textAlign: 'right',
                content: (s) => `${formatFloat(s.mermaPct)}%`,
            },
        ];
    }, [stats, selectedKeys, seriesLabel]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                {highlightProductionId != null ? (
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        ★ = corridas de esta orden de producción
                    </Typography>
                ) : null}
                {hasFocusedRun ? (
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        ◆ = corrida seleccionada
                    </Typography>
                ) : null}
            </Box>

            {/* Scatter: one series per checked employee/product */}
            <Box ref={graphRef} sx={{ height: 360, mb: 2 }}>
                {selectedStats.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography
                            variant={'body2'}
                            sx={{ color: 'text.secondary' }}
                        >
                            Marca filas en la tabla para verlas en la gráfica.
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width={'100%'} height={'100%'}>
                        <ScatterChart
                            margin={{
                                top: 16,
                                right: 24,
                                left: 8,
                                bottom: 8,
                            }}
                        >
                            <CartesianGrid strokeDasharray={'3 3'} />
                            <XAxis
                                type={'number'}
                                dataKey={'x'}
                                domain={['dataMin', 'dataMax']}
                                tick={{
                                    fontSize: 12,
                                    fill: theme.palette.text.primary,
                                }}
                                tickFormatter={(v) =>
                                    dayjs.utc(v).format('MM/YYYY')
                                }
                            />
                            <YAxis
                                type={'number'}
                                dataKey={'y'}
                                tick={{
                                    fontSize: 12,
                                    fill: theme.palette.text.primary,
                                }}
                                tickFormatter={(v) => formatFloat(v)}
                                width={64}
                            />
                            <ZAxis range={[60, 60]} />
                            <ZAxis zAxisId={'highlight'} range={[180, 180]} />
                            <ZAxis zAxisId={'focus'} range={[240, 240]} />
                            <Tooltip
                                content={
                                    <ScatterTooltip
                                        bg={theme.palette.background.paper}
                                        border={theme.palette.divider}
                                        color={theme.palette.text.primary}
                                    />
                                }
                            />
                            {selectedStats.map((s) => {
                                const idx = stats.findIndex(
                                    (x) => x.key === s.key,
                                );
                                const color = COLORS[idx % COLORS.length];
                                const isFocused = (p: ScatterPoint) =>
                                    runFocus != null &&
                                    p.orderProductionId ===
                                        runFocus.orderProductionId;
                                const normal = s.points.filter(
                                    (p) => !p.highlighted && !isFocused(p),
                                );
                                const highlighted = s.points.filter(
                                    (p) => p.highlighted && !isFocused(p),
                                );
                                const focused = s.points.filter((p) =>
                                    isFocused(p),
                                );
                                // Two series per key: the history as circles,
                                // this production's corridas as bigger stars,
                                // same color — so the graded run stands out
                                // against its own context.
                                return (
                                    <React.Fragment key={s.key}>
                                        <Scatter
                                            name={s.name}
                                            data={normal}
                                            fill={color}
                                            cursor={'pointer'}
                                            onClick={handleScatterClick}
                                        />
                                        {highlighted.length > 0 ? (
                                            <Scatter
                                                name={`${s.name} (esta orden)`}
                                                data={highlighted}
                                                fill={color}
                                                shape={'star'}
                                                zAxisId={'highlight'}
                                                cursor={'pointer'}
                                                onClick={handleScatterClick}
                                            />
                                        ) : null}
                                        {focused.length > 0 ? (
                                            <Scatter
                                                name={`${s.name} (seleccionada)`}
                                                data={focused}
                                                fill={color}
                                                stroke={
                                                    theme.palette.text.primary
                                                }
                                                strokeWidth={2}
                                                shape={'diamond'}
                                                zAxisId={'focus'}
                                                cursor={'pointer'}
                                                onClick={handleScatterClick}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </ScatterChart>
                    </ResponsiveContainer>
                )}
            </Box>

            {/* flexShrink: 0 — this stats table sits in the analysis panel's own
                DTabs flex column; TableContainer's overflow would otherwise let
                it collapse to a sliver. */}
            <Box sx={{ flexShrink: 0 }}>
                <CTableWrapper
                    items={tableItems}
                    data={stats}
                    loading={runsLoading}
                />
            </Box>
        </>
    );
}

// "Corridas" tab: machine/product-level hourly throughput, one row per
// production. Runs the same hourly query as the panel's KPI headline (identical
// variables → Apollo serves it from cache, no extra network round-trip); fetched
// here rather than passed down so the panel's `items` array can stay stable and
// not remount the tabs when the KPI data lands.
function CorridasTab({
    machineId,
    productId,
    fromDate,
    toDate,
}: IProductionPerformanceAnalysis) {
    const [queryHourly, { data, loading }] =
        useProductionPerformanceHourlyRunsLazyQuery();

    useEffect(() => {
        void queryHourly({
            variables: {
                machineId,
                productId,
                fromDate,
                toDate,
            },
        });
    }, [machineId, productId, fromDate, toDate, queryHourly]);

    const rows = useMemo<HourlyRunRow[]>(
        () => mapHourlyRows(data?.getMachineHourlyRuns ?? []),
        [data],
    );

    const tableItems = useMemo<ICustomTableWrapperProps<HourlyRunRow>['items']>(
        () => [
            {
                title: 'Fecha',
                textAlign: 'left',
                cellGrow: 2,
                content: (r) => r.date ?? '—',
            },
            {
                title: 'Productos',
                textAlign: 'right',
                content: (r) => formatFloat(r.productCount),
            },
            {
                title: 'Kilos producidos',
                textAlign: 'right',
                content: (r) => formatFloat(r.kilosProduced),
            },
            {
                title: 'Horas',
                textAlign: 'right',
                content: (r) => formatFloat(r.hoursProduced),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                content: (r) =>
                    r.kgHrProduced === null ? '—' : formatFloat(r.kgHrProduced),
            },
            {
                title: 'Kilos consumidos',
                textAlign: 'right',
                content: (r) => formatFloat(r.kilosConsumed),
            },
            {
                title: 'Horas',
                textAlign: 'right',
                content: (r) => formatFloat(r.hoursConsumed),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                content: (r) =>
                    r.kgHrConsumed === null ? '—' : formatFloat(r.kgHrConsumed),
            },
        ],
        [],
    );

    // flexShrink: 0 — same reason as the Gráfica stats table: keep the corridas
    // table at its natural height inside the analysis panel's flex column.
    return (
        <Box sx={{ flexShrink: 0 }}>
            <CTableWrapper items={tableItems} data={rows} loading={loading} />
        </Box>
    );
}

// Scatter tooltip — one point = one corrida (date + kilos).
function ScatterTooltip(props: any) {
    const { active, payload, bg, border, color } = props;
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div
            style={{
                backgroundColor: bg,
                border: `1px solid ${border}`,
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 12,
                color,
            }}
        >
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {p.date}
                {p.highlighted ? ' ★ esta orden' : ''}
            </div>
            <div>{formatFloat(p.y)} kg</div>
        </div>
    );
}
